// src/pages/RoomAllocation.jsx
import React, { useState, useEffect } from 'react';
import { getRooms, createRoom, updateRoom, deleteRoom } from '../services/roomService';
import { logActivity, ACTIVITY_TYPES } from '../services/activityService';
import { useUser } from '../context/UserContext';
import { useTenant } from '../context/TenantContext';
import RoomList from '../components/rooms/RoomList';
import RoomForm from '../components/rooms/RoomForm';
import RoomDetailModal from '../components/rooms/RoomDetailModal';
import { toast } from 'react-hot-toast';

export default function RoomAllocation() {
  const { user } = useUser();
  const { currentTenant, loading: tenantLoading } = useTenant();
  const [rooms, setRooms] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    if (!tenantLoading && currentTenant) {
      loadRooms();
    }
  }, [currentTenant, tenantLoading]);

  const loadRooms = async () => {
    try {
      setLoading(true);
      const roomsData = await getRooms();
      setRooms(roomsData);
    } catch (error) {
      console.error('Error loading rooms:', error);
      toast.error('Failed to load rooms');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (room) => {
    try {
      setLoading(true);
      let savedRoom;

      if (editing) {
        // Update existing room
        savedRoom = await updateRoom(room.id, room);
        setRooms(rooms.map((r) => (r.id === room.id ? savedRoom : r)));

        // Log activity
        await logActivity(
          ACTIVITY_TYPES.ROOM_UPDATED,
          { roomName: room.name, roomId: room.id },
          user?.userId || user?.id,
          user?.role
        );

        toast.success('Room updated successfully');
      } else {
        // Create new room
        savedRoom = await createRoom(room);
        setRooms([...rooms, savedRoom]);

        // Log activity
        await logActivity(
          ACTIVITY_TYPES.ROOM_CREATED,
          { roomName: room.name, roomId: savedRoom.id },
          user?.userId || user?.id,
          user?.role
        );

        toast.success('Room created successfully');
      }

      setEditing(null);
    } catch (error) {
      console.error('Error saving room:', error);
      toast.error('Failed to save room');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (room) => {
    setEditing(room);
    setShowAddForm(true);
  };

  const handleRoomClick = (room) => {
    setSelectedRoom(room);
  };

  const handleCloseDetail = () => {
    setSelectedRoom(null);
  };

  const handleAddNew = () => {
    setEditing(null);
    setShowAddForm(true);
  };

  const handleCancelForm = () => {
    setEditing(null);
    setShowAddForm(false);
  };

  const handleDelete = async (id) => {
    const roomToDelete = rooms.find(r => r.id === id);
    if (!roomToDelete) return;

    if (!window.confirm(`Delete room "${roomToDelete.name}"? This action cannot be undone.`)) return;

    try {
      setLoading(true);
      const success = await deleteRoom(id);

      if (success) {
        setRooms(rooms.filter((r) => r.id !== id));

        // Log activity
        await logActivity(
          ACTIVITY_TYPES.ROOM_UPDATED, // Using updated as we don't have a delete type
          { roomName: roomToDelete.name, roomId: id, action: 'deleted' },
          user?.userId || user?.id,
          user?.role
        );

        toast.success('Room deleted successfully');

        if (editing && editing.id === id) {
          setEditing(null);
          setShowAddForm(false);
        }
      } else {
        toast.error('Failed to delete room');
      }
    } catch (error) {
      console.error('Error deleting room:', error);
      toast.error('Failed to delete room');
    } finally {
      setLoading(false);
    }
  };

  if (tenantLoading || (loading && !currentTenant)) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading rooms...</p>
        </div>
      </div>
    );
  }

  if (!currentTenant) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <p className="text-gray-500">No tenant context available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Treatment Room Information</h1>
          <p className="text-gray-600 mt-1">View room details, availability, and manage clinic rooms</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500">
            {rooms.length} room{rooms.length !== 1 ? 's' : ''} total
          </div>
          <button
            onClick={handleAddNew}
            className="btn-primary"
            disabled={loading}
          >
            Add New Room
          </button>
        </div>
      </div>

      {/* Room List - Now comes first */}
      <RoomList
        rooms={rooms}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onRoomClick={handleRoomClick}
        loading={loading}
      />

      {/* Add/Edit Room Form - Now comes second and only shows when needed */}
      {showAddForm && (
        <div className="mt-8">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {editing ? 'Edit Room' : 'Add New Room'}
            </h2>
          </div>
          <RoomForm
            initialData={editing}
            onSave={handleSave}
            onCancel={handleCancelForm}
            loading={loading}
          />
        </div>
      )}

      {/* Room Detail Modal */}
      {selectedRoom && (
        <RoomDetailModal
          room={selectedRoom}
          onClose={handleCloseDetail}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}
